import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import $api from 'http'
import Notification from 'utils/Notifications'
import { getPublishedAssignments } from 'service/AssignmentsService'
import {
  PageHeader,
  UnvButton,
  Skeleton,
  EmptyState,
  ErrorState,
  Badge,
  DataTable,
  Card,
} from 'components'

import styles from './StudentDashboard.module.scss'

const fmtTime = (iso) => {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

const fmtDate = (iso) => {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('ru-RU')
  } catch {
    return ''
  }
}

const todayKey = () => new Date().toISOString().slice(0, 10)

const StudentDashboard = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  // Wave 4: title в табе браузера для главной страницы студента.
  useEffect(() => {
    document.title = `${t('studentDash.student', 'Студент')} — ЧУ`
  }, [t])
  const [me, setMe] = useState(null)
  const [profile, setProfile] = useState(null)
  const [lessons, setLessons] = useState([])
  const [assignments, setAssignments] = useState([])
  const [marks, setMarks] = useState([])
  const [progress, setProgress] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const loadAll = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const [meRes, stRes, lessRes, asRes] = await Promise.allSettled([
        $api.get('/admin-panel/me/'),
        $api.get('/students/me/'),
        $api.get('/academic/lessons/my-week/', { params: { days: 7 } }),
        getPublishedAssignments(),
      ])

      const meData = meRes.status === 'fulfilled' ? meRes.value.data : null
      setMe(meData)
      const stData = stRes.status === 'fulfilled' ? stRes.value.data : null
      setProfile(stData)
      setLessons(lessRes.status === 'fulfilled' ? (lessRes.value.data?.results || lessRes.value.data || []) : [])
      setAssignments(asRes.status === 'fulfilled' ? (asRes.value.data?.results || asRes.value.data || []) : [])

      const studentId = stData?.id
      if (studentId) {
        const [mRes, pRes] = await Promise.allSettled([
          $api.get('/academic/marks/', { params: { student: studentId } }),
          $api.get('/academic/progress/', { params: { student: studentId } }),
        ])
        setMarks(mRes.status === 'fulfilled' ? (mRes.value.data?.results || mRes.value.data || []) : [])
        setProgress(pRes.status === 'fulfilled' ? (pRes.value.data?.results || pRes.value.data || []) : [])
      }
    } catch (e) {
      setLoadError(true)
      setNotify({ isOpen: true, message: t('common.loading_failed'), type: 'error', sound: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const todayLessons = useMemo(() => {
    const k = todayKey()
    return (lessons || [])
      .filter((l) => {
        const d = l.date || (l.start_at ? l.start_at.slice(0, 10) : null)
        return d === k
      })
      .sort((a, b) => (a.start_at || '').localeCompare(b.start_at || ''))
  }, [lessons])

  const upcomingDeadlines = useMemo(() => {
    return [...(assignments || [])]
      .filter((a) => a.due_at)
      .sort((a, b) => new Date(a.due_at) - new Date(b.due_at))
      .slice(0, 5)
  }, [assignments])

  const progressSummary = useMemo(() => {
    const total = progress.length
    const done = progress.filter((p) => p.status === 'done').length
    return { total, done, percent: total ? Math.round((done / total) * 100) : 0 }
  }, [progress])

  const recentMarks = useMemo(() => {
    return [...(marks || [])]
      .sort((a, b) => new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0))
      .slice(0, 10)
  }, [marks])

  const isCurrentLesson = (l) => {
    if (!l.start_at || !l.end_at) return false
    const now = Date.now()
    return new Date(l.start_at).getTime() <= now && now <= new Date(l.end_at).getTime()
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <Skeleton variant="card" />
        <div className={styles.grid}>
          <Skeleton variant="card" />
          <Skeleton variant="card" />
          <Skeleton variant="card" />
          <Skeleton variant="table" rows={5} cols={4} />
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className={styles.page}>
        <ErrorState title={t('common.loading_failed')} onRetry={loadAll} />
        <Notification notify={notify} setNotify={setNotify} />
      </div>
    )
  }

  const subtitleParts = [
    `${t('studentDash.inn_label')}: ${profile?.inn || '—'}`,
    `${t('studentDash.group_label')}: ${profile?.group_name || profile?.group?.name || '—'}`,
    `${t('studentDash.course_label')}: ${profile?.course || '—'}`,
    `${t('studentDash.form_label')}: ${profile?.education_form || profile?.form || '—'}`,
  ].join(' · ')

  return (
    <div className={styles.page}>
      <PageHeader
        title={me?.full_name || profile?.full_name || t('studentDash.student')}
        subtitle={subtitleParts}
        actions={
          <div className={styles.quick}>
            <UnvButton variant="ghost" iconLeft="calendar" onClick={() => navigate('/schedule/')}>
              {t('studentDash.schedule_week')}
            </UnvButton>
            <UnvButton variant="ghost" iconLeft="book" onClick={() => navigate('/syllabus-view/')}>
              {t('studentDash.study_plan')}
            </UnvButton>
            <UnvButton variant="ghost" iconLeft="gradebook" onClick={() => navigate('/gradebook/')}>
              {t('studentDash.gradebook_btn')}
            </UnvButton>
            <UnvButton variant="ghost" iconLeft="file" onClick={() => navigate('/statements/')}>
              {t('studentDash.statements_btn')}
            </UnvButton>
          </div>
        }
      />

      <div className={styles.grid}>
        <div className={`unv-card ${styles.widget}`}>
          <div className={styles.widgetHeader}>
            <h3>{t('common.today')}</h3>
            <span className={styles.muted}>{new Date().toLocaleDateString('ru-RU')}</span>
          </div>
          {todayLessons.length === 0 ? (
            <EmptyState iconName="calendar" title={t('studentDash.no_lessons_today')} variant="compact" />
          ) : (
            <ul className={styles.lessonList}>
              {todayLessons.map((l) => (
                <li
                  key={l.id}
                  className={`${styles.lessonItem} ${isCurrentLesson(l) ? styles.lessonNow : ''}`}
                >
                  <div className={styles.lessonTime}>
                    {fmtTime(l.start_at)}–{fmtTime(l.end_at)}
                  </div>
                  <div className={styles.lessonBody}>
                    <div className={styles.lessonSubject}>{l.subject_name || l.subject?.name || '—'}</div>
                    <div className={styles.lessonMeta}>
                      <span>{t('studentDash.auditorium')} {l.room || l.classroom || '—'}</span>
                      <span>{l.teacher_name || l.teacher?.full_name || '—'}</span>
                      {l.kind && <Badge variant="info">{l.kind}</Badge>}
                    </div>
                  </div>
                  {l.status && <Badge variant={isCurrentLesson(l) ? 'success' : 'muted'} dot>{l.status}</Badge>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={`unv-card ${styles.widget}`}>
          <div className={styles.widgetHeader}>
            <h3>{t('studentDash.upcoming_deadlines')}</h3>
            <UnvButton variant="ghost" size="sm" onClick={() => navigate('/assignments-list/')}>
              {t('studentDash.all_assignments')}
            </UnvButton>
          </div>
          {upcomingDeadlines.length === 0 ? (
            <EmptyState iconName="check" title={t('studentDash.no_active_deadlines')} variant="compact" />
          ) : (
            <ul className={styles.deadlineList}>
              {upcomingDeadlines.map((a) => (
                <li key={a.id} className={styles.deadlineItem}>
                  <div>
                    <div className={styles.deadlineTitle}>{a.title}</div>
                    <div className={styles.muted}>{a.subject_name || a.subject?.name || ''}</div>
                  </div>
                  <div className={styles.deadlineDate}>{fmtDate(a.due_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={`unv-card ${styles.widget}`}>
          <div className={styles.widgetHeader}>
            <h3>{t('studentDash.course_progress')}</h3>
          </div>
          <div className={styles.progressBlock}>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} style={{ width: `${progressSummary.percent}%` }} />
            </div>
            <div className={styles.progressMeta}>
              <span>
                {t('studentDash.topics_done', { done: progressSummary.done, total: progressSummary.total })}
              </span>
              <span>{progressSummary.percent}%</span>
            </div>
          </div>
        </div>

        <div className={`unv-card ${styles.widget}`}>
          <div className={styles.widgetHeader}>
            <h3>{t('studentDash.recent_grades')}</h3>
          </div>
          {recentMarks.length === 0 ? (
            <EmptyState iconName="folder" title={t('studentDash.no_grades')} variant="compact" />
          ) : (
            <DataTable
              columns={[
                { key: 'date', label: t('common.date'), render: (_, r) => fmtDate(r.created_at || r.date) },
                { key: 'subject', label: t('studentDash.subject'), render: (_, r) => r.subject_name || r.subject?.name || '—' },
                { key: 'kind', label: t('studentDash.kind'), render: (_, r) => r.kind || '—' },
                {
                  key: 'value',
                  label: t('studentDash.grade'),
                  render: (_, r) => {
                    const v = r.value ?? r.score
                    const num = Number(v)
                    let variant = 'muted'
                    if (!Number.isNaN(num)) {
                      if (num >= 4.5) variant = 'success'
                      else if (num >= 3) variant = 'info'
                      else if (num > 0) variant = 'warning'
                    }
                    return <Badge variant={variant}>{v ?? '—'}</Badge>
                  },
                },
              ]}
              data={recentMarks}
              searchable={false}
              pageSize={10}
              emptyText={t('studentDash.no_grades')}
            />
          )}
        </div>

      </div>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default StudentDashboard
